import { useState, useCallback } from 'react';
import { submitContractorAnonymous, submitContractorAuthenticated } from '@/api/contractorApi';
import { useSession } from '@/contexts/AuthContext';
import type { ContractorInput, ContractorReport } from '@/types';

interface UseContractorReportResult {
  report: ContractorReport | null;
  loading: boolean;
  error: string | null;
  submit: (payload: ContractorInput | Partial<ContractorInput>, useAuth?: boolean) => Promise<{ success: boolean; error?: string }>;
  clearReport: () => void;
}

/**
 * Hook for submitting contractor calculations.
 *
 * Automatically uses authenticated endpoint when user is logged in (unless overridden).
 * Uses anonymous endpoint for logged-out users.
 */
export function useContractorReport(): UseContractorReportResult {
  const { user } = useSession();
  const [report, setReport] = useState<ContractorReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = useCallback(
    async (payload: ContractorInput | Partial<ContractorInput>, useAuth?: boolean): Promise<{ success: boolean; error?: string }> => {
      setLoading(true);
      setError(null);

      try {
        // Determine which endpoint to use
        // Default: use authenticated if user is logged in, otherwise anonymous
        const shouldUseAuth = useAuth !== undefined ? useAuth : !!user;

        let result: ContractorReport;
        if (shouldUseAuth && user) {
          // Authenticated: merge with saved settings
          result = await submitContractorAuthenticated(payload as Partial<ContractorInput>);
        } else {
          // Anonymous: use full payload
          result = await submitContractorAnonymous(payload as ContractorInput);
        }

        setReport(result);
        return { success: true };
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to calculate contractor taxes';
        setError(message);
        setReport(null);
        return { success: false, error: message };
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  const clearReport = useCallback(() => {
    setReport(null);
    setError(null);
  }, []);

  return {
    report,
    loading,
    error,
    submit,
    clearReport,
  };
}
